// Expense (accounting) helpers — money math for the VAT / WHT amount object,
// Thai month names, and formatting. Shared by the Expense page + editor.

export const TH_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]
export const TH_MONTHS_ABBR = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

// Document-type tags for an uploaded expense file (stored in file[].tag).
export const FILE_TAGS = ['ใบเสนอราคา', 'ใบกำกับภาษี', 'ใบแจ้งหนี้', 'ใบเสร็จ', 'ภาษีหัก ณ ที่จ่าย', 'อื่นๆ']

export const VAT_RATE = 0.07
// Common Thai withholding-tax rates (%) with a short label + the income type
// (ประเภทเงินได้) that auto-fills when the rate is chosen.
export const WHT_PRESETS = [
  { percent: 1, type: 'ค่าขนส่ง', label: '1% — ค่าขนส่ง' },
  { percent: 2, type: 'ค่าโฆษณา', label: '2% — ค่าโฆษณา' },
  { percent: 3, type: 'ค่าบริการ', label: '3% — ค่าบริการ/รับเหมา' },
  { percent: 5, type: 'ค่าเช่า', label: '5% — ค่าเช่า' },
]

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}
// round to 2 dp, killing binary-float dust
const r2 = (n) => Math.round((num(n) + Number.EPSILON) * 100) / 100

/**
 * Compute the full `amount` object from the editable inputs.
 *
 * Inputs:
 *   price        the price the user types
 *   discount     discount applied before VAT
 *   VAT_add      VAT (7%) is applicable
 *   VAT_include  the typed price ALREADY includes VAT (extract it) vs add on top
 *   WHT_add      withhold tax at source
 *   WHT_percent  WHT rate (%)
 *   WHT_type     WHT description
 *
 * Flow: afterDiscount → split net/VAT → WHT on the net base → payment.
 * Mirrors the standard Thai purchase-invoice calc.
 */
export function computeAmount({
  price = 0, discount = 0,
  VAT_add = false, VAT_include = false,
  WHT_add = false, WHT_percent = 0, WHT_type = '',
} = {}) {
  const afterDiscount = r2(num(price) - num(discount))

  // VAT_include ("รวม VAT แล้ว") implies VAT is present even if VAT_add isn't set.
  let base, vat
  if (VAT_add || VAT_include) {
    if (VAT_include) {
      base = r2(afterDiscount / (1 + VAT_RATE)) // typed amount is gross → extract
      vat  = r2(afterDiscount - base)
    } else {
      base = afterDiscount                       // typed amount is net → add on top
      vat  = r2(afterDiscount * VAT_RATE)
    }
  } else {
    base = afterDiscount
    vat  = 0
  }
  const afterVAT = r2(base + vat)

  // WHT is computed on the pre-VAT base (Thai standard).
  const wht = WHT_add ? r2(base * (num(WHT_percent) / 100)) : 0
  const afterWHT = r2(afterVAT - wht)

  return {
    discount: r2(discount),
    total_price: base,                 // ราคาก่อน VAT (net)
    VAT: vat,                          // VAT amount
    VAT_add: !!VAT_add,
    VAT_include: !!VAT_include,
    total_price_afterVAT: afterVAT,    // ราคาหลัง VAT (gross)
    WHT: wht,
    WHT_add: !!WHT_add,
    WHT_percent: num(WHT_percent),
    WHT_type: WHT_type || '',
    total_price_afterWHT: afterWHT,
    payment: afterWHT,                 // ยอดชำระรวม — what we actually pay
  }
}

// Money formatting: 1,234.00 (always 2 dp).
export function fmtMoney(n) {
  return num(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
// Whole-baht formatting for compact stats.
export function fmtBaht(n) {
  return Math.round(num(n)).toLocaleString('en-US')
}

// Sum a billing[] line array's price_total (price × N fallback).
export function billingTotal(billing) {
  if (!Array.isArray(billing)) return 0
  return billing.reduce((s, b) => s + (num(b?.price_total) || num(b?.price) * num(b?.N)), 0)
}

// Year/month stamps from a JS Date (or ISO string) → the record's period fields.
// `expense_month` stores the ABBREVIATED Thai month (มิ.ย. / ก.ค.) to match the
// legacy records, not the full name.
export function periodFields(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike)
  if (Number.isNaN(d.getTime())) return { expense_year: 0, expense_month_num: 0, expense_month: '' }
  const m = d.getMonth()
  return { expense_year: d.getFullYear(), expense_month_num: m + 1, expense_month: TH_MONTHS_ABBR[m] }
}

// Pull the human message out of an api.js error ("HTTP 400: {json}") — Xano
// preconditions return { code, message } and we want to show the message.
export function xanoMessage(e) {
  const s = String(e?.message || e || '')
  const m = s.match(/\{[\s\S]*\}/)
  if (m) { try { const o = JSON.parse(m[0]); if (o && o.message) return o.message } catch { /* ignore */ } }
  return s.replace(/^HTTP\s+\d+:\s*/, '') || 'เกิดข้อผิดพลาด'
}
